import { useState, useCallback, useEffect, useRef } from "react";
import Auth from "../../../../Auth";

type Direction = "up" | "down" | "left" | "right";
type SaveTimer = ReturnType<typeof setTimeout>;
const SAVE_IDLE_DELAY_MS = 750;
const MAX_FOLDER_DROP_NESTING_LEVEL = 19;

interface UseKeyboardReorderOptions {
  project: any;
  displayTree: any[];
  setDisplayTree: (tree: any[]) => void;
  suppressProjectTreeSyncRef: React.MutableRefObject<boolean>;
  revealFolder?: (folderId: string) => void;
}

interface UseKeyboardReorderResult {
  onKeyboardMove: (nodeId: string, direction: Direction) => void;
  cancelPendingKeyboardSave: () => { nodes: any[]; projectUpdate: any } | null;
  announcement: string;
  lastMovedNodeIdRef: React.MutableRefObject<string | null>;
}

function cloneNode(node: any) {
  const { children, ...rest } = node;
  return { ...rest };
}

export function buildKeyboardReorderTree(nodes: any[]) {
  const clonedNodes = nodes.map(cloneNode).sort((a, b) => a.displayOrder - b.displayOrder);
  const nodesByParent = clonedNodes.reduce((acc: Record<string, any[]>, node) => {
    const parentId = node.parentId || "none";
    if (!acc[parentId]) acc[parentId] = [];
    acc[parentId].push(node);
    return acc;
  }, {});

  clonedNodes.forEach((node) => {
    if (node.type === "folder") {
      node.children = nodesByParent[node.id] || [];
    }
  });

  return nodesByParent.none || [];
}

function getChildren(nodes: any[], parentId: string | null | undefined) {
  const normalizedParentId = parentId || "none";
  return nodes
    .filter((node) => (node.parentId || "none") === normalizedParentId)
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

function normalizeParentId(parentId: string | null | undefined) {
  return parentId && parentId !== "none" ? parentId : "none";
}

function getNodeDepth(nodes: any[], nodeId: string) {
  let depth = 0;
  let current = nodes.find((node) => node.id === nodeId);
  const visited = new Set<string>();

  while (current?.parentId && current.parentId !== "none") {
    if (visited.has(current.id)) break;
    visited.add(current.id);
    const parent = nodes.find((node) => node.id === current.parentId);
    if (!parent) break;
    depth += 1;
    current = parent;
  }

  return depth;
}

export function moveKeyboardReorderNode(nodes: any[], nodeId: string, destinationParentId: string | null | undefined, destinationIndex: number) {
  const movingNode = nodes.find((node) => node.id === nodeId);
  if (!movingNode) return nodes;

  const destinationParent = normalizeParentId(destinationParentId);
  const nodesByParent = new Map<string, any[]>();

  nodes.forEach((node) => {
    if (node.id === nodeId) return;
    const parentId = normalizeParentId(node.parentId);
    if (!nodesByParent.has(parentId)) nodesByParent.set(parentId, []);
    nodesByParent.get(parentId)!.push(cloneNode(node));
  });

  nodesByParent.forEach((siblings) => siblings.sort((a, b) => a.displayOrder - b.displayOrder));

  const destinationSiblings = nodesByParent.get(destinationParent) || [];
  const insertIndex = Math.max(0, Math.min(destinationIndex, destinationSiblings.length));
  destinationSiblings.splice(insertIndex, 0, {
    ...cloneNode(movingNode),
    parentId: destinationParent,
  });
  nodesByParent.set(destinationParent, destinationSiblings);

  const renumbered: any[] = [];
  nodesByParent.forEach((siblings, parentId) => {
    siblings.forEach((node, index) => {
      renumbered.push({
        ...node,
        parentId,
        displayOrder: index + 1,
      });
    });
  });

  return renumbered;
}

export function buildKeyboardReorderProjectUpdate(project: any, nodes: any[]) {
  const positions = new Map(
    nodes.map((node) => [
      node.id,
      {
        parentId: normalizeParentId(node.parentId),
        displayOrder: node.displayOrder,
      },
    ])
  );

  return {
    folders: (project.folders || []).map((folder: any) => {
      const position = positions.get(folder.id);
      return position ? { ...folder, ...position } : folder;
    }),
    contents: {
      ...project.data.contents,
      contents: project.data.contents.contents.map((document: any) => {
        const position = positions.get(document.id);
        return position ? { ...document, ...position } : document;
      }),
    },
  };
}

function persistProjectUpdate(project: any, projectUpdate: any, shouldSkipSave: () => boolean) {
  return project.queue.enqueue(() =>
    shouldSkipSave() ? Promise.resolve() : Auth.fetchWithIdToken(`/api/projects/${project.id}`, {
      method: "PUT",
      ...projectUpdate,
    })
  );
}

export function useKeyboardReorder({
  project,
  displayTree,
  setDisplayTree,
  suppressProjectTreeSyncRef,
  revealFolder,
}: UseKeyboardReorderOptions): UseKeyboardReorderResult {
  const [announcement, setAnnouncement] = useState("");
  const lastMovedNodeIdRef = useRef<string | null>(null);
  const optimisticNodesRef = useRef<any[]>([]);
  const saveTimerRef = useRef<SaveTimer | null>(null);
  const saveInFlightRef = useRef(false);
  const isMountedRef = useRef(true);
  const latestSaveRef = useRef<{ version: number; projectUpdate: any; failureAnnouncement: string } | null>(null);
  const saveVersionRef = useRef(0);
  const cancelledThroughSaveVersionRef = useRef(0);

  useEffect(() => {
    if (suppressProjectTreeSyncRef.current) {
      return;
    }
    optimisticNodesRef.current = project.getAllTreeNodes();
  }, [project, displayTree, suppressProjectTreeSyncRef]);

  const flushLatestSave = useCallback(async () => {
    if (saveInFlightRef.current) {
      return;
    }
    const save = latestSaveRef.current;
    if (!save) {
      return;
    }

    saveInFlightRef.current = true;
    let saveFailed = false;

    try {
      if (cancelledThroughSaveVersionRef.current < save.version) {
        await persistProjectUpdate(
          project,
          save.projectUpdate,
          () => cancelledThroughSaveVersionRef.current >= save.version
        );
      }
    } catch (error) {
      saveFailed = true;
    } finally {
      saveInFlightRef.current = false;

      if (cancelledThroughSaveVersionRef.current >= save.version) {
        if (latestSaveRef.current && latestSaveRef.current.version !== save.version) {
          await flushLatestSave();
        }
        return;
      }

      if (latestSaveRef.current?.version === save.version) {
        latestSaveRef.current = null;
        let refreshedAfterFailure = false;
        if (saveFailed) {
          await project.fetch();
          refreshedAfterFailure = true;
          if (isMountedRef.current) {
            setAnnouncement(save.failureAnnouncement);
          }
        } else {
          project.mergeData(save.projectUpdate);
          project.onChange?.(project);
        }
        optimisticNodesRef.current = project.getAllTreeNodes();
        if (refreshedAfterFailure && isMountedRef.current) {
          setDisplayTree(project.getRootTree());
        }
        suppressProjectTreeSyncRef.current = false;
      } else {
        await flushLatestSave();
      }
    }
  }, [project, setDisplayTree, suppressProjectTreeSyncRef]);

  const scheduleSave = useCallback(
    (projectUpdate: any, failureAnnouncement: string) => {
      saveVersionRef.current += 1;
      latestSaveRef.current = {
        version: saveVersionRef.current,
        projectUpdate,
        failureAnnouncement,
      };

      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }

      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        flushLatestSave();
      }, SAVE_IDLE_DELAY_MS);
    },
    [flushLatestSave]
  );

  const cancelPendingKeyboardSave = useCallback(() => {
    const canceledSave = latestSaveRef.current;
    const canceledNodes = canceledSave ? optimisticNodesRef.current : null;

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    if (latestSaveRef.current) {
      cancelledThroughSaveVersionRef.current = Math.max(
        cancelledThroughSaveVersionRef.current,
        latestSaveRef.current.version
      );
      latestSaveRef.current = null;
    }

    optimisticNodesRef.current = project.getAllTreeNodes();
    lastMovedNodeIdRef.current = null;
    suppressProjectTreeSyncRef.current = false;

    return canceledSave && canceledNodes
      ? {
        nodes: canceledNodes,
        projectUpdate: canceledSave.projectUpdate,
      }
      : null;
  }, [project, suppressProjectTreeSyncRef]);

  useEffect(() => {
    isMountedRef.current = true;

    const flushPendingSave = () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      flushLatestSave();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushPendingSave();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", flushPendingSave);

    return () => {
      isMountedRef.current = false;
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
        flushPendingSave();
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", flushPendingSave);
    };
  }, [flushLatestSave]);

  const onKeyboardMove = useCallback(
    (nodeId: string, direction: Direction) => {
      const allNodes = optimisticNodesRef.current.length > 0
        ? optimisticNodesRef.current
        : project.getAllTreeNodes();
      const node = allNodes.find((n: any) => n.id === nodeId);
      if (!node) {
        return;
      }

      const siblings = getChildren(allNodes, node.parentId);
      const siblingIndex = siblings.findIndex((s: any) => s.id === nodeId);
      const nodeName = node.name || node.title || "item";

      let nextNodes: any[] | null = null;
      let announcementText = "";

      switch (direction) {
        case "up": {
          if (siblingIndex <= 0) {
            return;
          }
          nextNodes = moveKeyboardReorderNode(allNodes, nodeId, node.parentId, siblingIndex - 1);
          announcementText = `Moved ${nodeName} up`;
          break;
        }
        case "down": {
          if (siblingIndex >= siblings.length - 1) {
            return;
          }
          nextNodes = moveKeyboardReorderNode(allNodes, nodeId, node.parentId, siblingIndex + 1);
          announcementText = `Moved ${nodeName} down`;
          break;
        }
        case "left": {
          if (!node.parentId || node.parentId === "none") {
            return;
          }
          const parent = allNodes.find((n: any) => n.id === node.parentId);
          if (!parent) {
            return;
          }
          const parentSiblings = getChildren(allNodes, parent.parentId);
          const parentIndex = parentSiblings.findIndex(
            (s: any) => s.id === parent.id
          );
          nextNodes = moveKeyboardReorderNode(allNodes, nodeId, parent.parentId, parentIndex + 1);
          announcementText = `Moved ${nodeName} out of ${parent.name || "folder"}`;
          break;
        }
        case "right": {
          if (siblingIndex <= 0) {
            return;
          }
          const prevSibling = siblings[siblingIndex - 1];
          if (prevSibling.type !== "folder") {
            return;
          }
          if (getNodeDepth(allNodes, prevSibling.id) >= MAX_FOLDER_DROP_NESTING_LEVEL) {
            return;
          }
          nextNodes = moveKeyboardReorderNode(allNodes, nodeId, prevSibling.id, 0);
          announcementText = `Moved ${nodeName} into ${prevSibling.name || "folder"}`;
          revealFolder?.(prevSibling.id);
          break;
        }
      }

      if (!nextNodes) return;
      const projectUpdate = buildKeyboardReorderProjectUpdate(project, nextNodes);

      lastMovedNodeIdRef.current = nodeId;
      setAnnouncement(announcementText);

      optimisticNodesRef.current = nextNodes;
      setDisplayTree(buildKeyboardReorderTree(optimisticNodesRef.current));
      suppressProjectTreeSyncRef.current = true;
      scheduleSave(projectUpdate, `Could not move ${nodeName}.`);
    },
    [project, setDisplayTree, suppressProjectTreeSyncRef, revealFolder, scheduleSave]
  );

  return { onKeyboardMove, cancelPendingKeyboardSave, announcement, lastMovedNodeIdRef };
}
